import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { GlassCard, Chip, Skeleton, Toggle, Table, PageHead, Bar } from "@/v2/admin/ui";
import { useDept, downloadDeptPdf } from "./DeptShell";
import { FileText, Download, Eye, Loader2, Building2, User } from "lucide-react";

const dateBefore = (d) => new Date(Date.now() - d * 864e5).toISOString().slice(0, 10);

export default function DeptReports() {
  const { q, department, meta } = useDept();
  const [kind, setKind] = useState("department");
  const [from, setFrom] = useState(dateBefore(30));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [uid, setUid] = useState("");
  const [opts, setOpts] = useState({ mode: "detailed", include_charts: true, include_details: true, hide_names: false });
  const [busy, setBusy] = useState(false);

  const url = kind === "department"
    ? `/dept/reports/summary?${q}&date_from=${from}&date_to=${to}`
    : `/dept/reports/employee/${uid}?${q}`;
  const ready = kind === "department" || !!uid;

  const { data, isLoading, error } = useQuery({
    queryKey: ["dept-rep", q, kind, from, to, uid],
    queryFn: async () => (await api.get(url)).data,
    enabled: ready,
  });

  const exportPdf = async () => {
    setBusy(true);
    try {
      await downloadDeptPdf({ kind, user_id: uid, date_from: from, date_to: to, department, options: opts },
        `masar-dept-${kind}-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("تم إنشاء التقرير بصيغة PDF");
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر إنشاء التقرير"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="dept-reports">
      <PageHead icon={FileText} title="تقارير الإدارة" sub="تقارير رسمية عربية جاهزة للطباعة عن إدارتك وموظفيها." />

      <div className="grid lg:grid-cols-[320px_1fr] gap-6 items-start">
        <GlassCard className="p-5 space-y-4">
          <div>
            <label className="v2-label">نوع التقرير</label>
            <div className="grid grid-cols-2 gap-2">
              {[["department", "تقرير الإدارة", Building2], ["employee", "تقرير موظف", User]].map(([k, l, I]) => (
                <button key={k} onClick={() => setKind(k)} data-testid={`dept-rep-kind-${k}`}
                  className={`h-11 rounded-2xl text-xs font-bold flex items-center justify-center gap-1.5 ${kind === k ? "bg-gradient-to-l from-[#e3c799] to-[#bb9661] text-[#25343f]" : "bg-white/5 border border-white/10 text-[#bcc8cf]"}`}>
                  <I size={14} /> {l}
                </button>
              ))}
            </div>
          </div>

          {kind === "department" ? (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="v2-label">من</label><input type="date" dir="ltr" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="dept-rep-from" className="v2-input" /></div>
              <div><label className="v2-label">إلى</label><input type="date" dir="ltr" value={to} onChange={(e) => setTo(e.target.value)} data-testid="dept-rep-to" className="v2-input" /></div>
            </div>
          ) : (
            <div><label className="v2-label">الموظف</label>
              <select value={uid} onChange={(e) => setUid(e.target.value)} data-testid="dept-rep-user" className="v2-input">
                <option value="">اختر الموظف...</option>
                {(meta?.employees || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="v2-label">شكل التقرير</label>
            <div className="flex gap-2">
              {[["detailed", "تفصيلي"], ["executive", "مختصر تنفيذي"]].map(([k, l]) => (
                <button key={k} onClick={() => setOpts({ ...opts, mode: k })} data-testid={`dept-rep-mode-${k}`}
                  className={`flex-1 h-10 rounded-full text-xs font-bold ${opts.mode === k ? "bg-[#91cab8] text-[#25343f]" : "bg-white/5 border border-white/10 text-[#bcc8cf]"}`}>{l}</button>
              ))}
            </div>
          </div>
          <Toggle checked={opts.include_charts} onChange={(v) => setOpts({ ...opts, include_charts: v })} label="إظهار الرسوم البيانية" testId="dept-rep-charts" />
          <Toggle checked={opts.include_details} onChange={(v) => setOpts({ ...opts, include_details: v })} label="إظهار الجداول التفصيلية" testId="dept-rep-details" />
          <Toggle checked={opts.hide_names} onChange={(v) => setOpts({ ...opts, hide_names: v })} label="إخفاء أسماء الموظفين" testId="dept-rep-hidenames" />

          <button onClick={exportPdf} disabled={busy || !ready || !data} data-testid="dept-rep-pdf" className="v2-btn w-full !h-12">
            {busy ? <><Loader2 size={16} className="animate-spin" /> جارٍ إنشاء PDF...</> : <><Download size={16} /> تحميل التقرير PDF</>}
          </button>
        </GlassCard>

        <div className="space-y-4">
          {!ready ? (
            <GlassCard className="p-12 text-center text-[#bcc8cf] text-sm">اختر الموظف لعرض المعاينة.</GlassCard>
          ) : isLoading ? <Skeleton className="h-80 w-full" /> : error ? (
            <GlassCard className="p-12 text-center text-[#F7A08A] text-sm">تعذّر تحميل بيانات التقرير.</GlassCard>
          ) : data && (
            <>
              <GlassCard gold className="p-5" data-testid="dept-rep-preview">
                <div className="flex items-center gap-2 mb-3">
                  <Eye size={16} className="text-[#e3c799]" />
                  <h3 className="font-bold">معاينة التقرير</h3>
                  <Chip tone="muted">{opts.mode === "executive" ? "مختصر تنفيذي" : "تفصيلي"}</Chip>
                </div>
                <p className="text-sm leading-relaxed text-[#f6f8fa]/85 v2-inset p-4">{data.summary_text}</p>
              </GlassCard>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(data.kpis || []).map(([l, v]) => (
                  <GlassCard key={l} className="p-4 text-center">
                    <div className="text-xl font-extrabold v2-gold-text">{v}</div>
                    <div className="text-[10px] text-[#bcc8cf] mt-1">{l}</div>
                  </GlassCard>
                ))}
              </div>

              {opts.include_charts && data.donut != null && (
                <GlassCard className="p-5">
                  <div className="flex justify-between text-xs text-[#bcc8cf] mb-2"><span>{data.donut_label}</span><span>{data.donut}%</span></div>
                  <Bar value={data.donut} />
                </GlassCard>
              )}

              {opts.include_charts && (data.bars || []).length > 0 && (
                <GlassCard className="p-5 space-y-2">
                  <h4 className="font-bold text-sm mb-2">{data.bars_title}</h4>
                  {data.bars.map(([l, v], i) => (
                    <div key={i}>
                      <div className="flex justify-between text-[11px] text-[#bcc8cf] mb-1"><span className="truncate max-w-[70%]">{l}</span><span>{v}</span></div>
                      <Bar value={Math.min(100, Number(v))} />
                    </div>
                  ))}
                </GlassCard>
              )}

              {opts.include_details && opts.mode !== "executive" && (data.tables || []).map((t, i) => (
                <div key={i} className="space-y-2">
                  <h4 className="font-bold text-sm">{t.title}</h4>
                  <Table head={t.headers} min={620} testId={`dept-rep-table-${i}`}>
                    {t.rows.map((r, ri) => (
                      <tr key={ri}>{r.map((c, ci) => (
                        <td key={ci} className={ci === 0 ? "font-semibold" : "text-[#bcc8cf]"}>
                          {opts.hide_names && t.name_col === ci ? "موظف ****" : String(c)}
                        </td>
                      ))}</tr>
                    ))}
                  </Table>
                </div>
              ))}

              {(data.analysis || []).length > 0 && (
                <GlassCard className="p-5">
                  <h4 className="font-bold text-sm mb-2">تحليل النتائج وأهم الملاحظات</h4>
                  <ul className="space-y-1.5 text-sm text-[#f6f8fa]/85">
                    {data.analysis.map((a, i) => <li key={i} className="flex gap-2"><span className="text-[#e3c799]">•</span> {a}</li>)}
                  </ul>
                </GlassCard>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
